/**
 * SenritsuX High-Resolution Cover Art Processing Engine
 * 
 * Provides:
 * - Image type and size validation (JPG, PNG, WEBP).
 * - 1:1 Aspect ratio validation and non-distorting square cropping.
 * - High-quality Canvas 2D resampling to exact 3000 × 3000 px JPEG at 0.95 quality.
 * - Real-time progress updates.
 */

export const SUPPORTED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];
export const SUPPORTED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
export const MAX_COVER_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB max raw upload

/**
 * Validate cover art file before processing
 * @param {File} file
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateCoverFile(file) {
  if (!file) {
    return { valid: false, error: 'No cover art file selected.' };
  }

  const name = file.name.toLowerCase();
  const hasValidExt = SUPPORTED_IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
  const mime = (file.type || '').toLowerCase();
  const hasValidMime = mime ? SUPPORTED_IMAGE_MIME_TYPES.includes(mime) : true;

  if (!hasValidExt || !hasValidMime) {
    return {
      valid: false,
      error: `Unsupported image format: "${file.name}". Please upload a JPG, PNG, or WEBP image.`,
    };
  }

  if (file.size > MAX_COVER_SIZE_BYTES) {
    return {
      valid: false,
      error: `File size exceeds limit (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed size is 25 MB.`,
    };
  }

  if (file.size === 0) {
    return {
      valid: false,
      error: `The file "${file.name}" is empty (0 bytes).`,
    };
  }

  return { valid: true };
}

/**
 * Process, validate, square-crop (if needed), and resample cover art to 3000x3000px JPG.
 * 
 * @param {File} file - Raw uploaded image file
 * @param {(status: { stage: string, progress: number, message: string }) => void} [onProgress]
 * @returns {Promise<{
 *   processedFile: File,
 *   previewUrl: string,
 *   originalFilename: string,
 *   processedFilename: string,
 *   originalWidth: number,
 *   originalHeight: number,
 *   finalWidth: number,
 *   finalHeight: number,
 *   aspectRatio: string,
 *   size: number
 * }>}
 */
export async function processCoverArt(file, onProgress) {
  const validation = validateCoverFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  if (onProgress) {
    onProgress({ stage: 'reading', progress: 15, message: 'Loading image data...' });
  }

  // Load image into HTMLImageElement
  const image = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image file. Corrupted or invalid image.'));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.readAsDataURL(file);
  });

  const originalWidth = image.naturalWidth || image.width;
  const originalHeight = image.naturalHeight || image.height;

  if (originalWidth < 500 || originalHeight < 500) {
    throw new Error(`Cover image resolution too low (${originalWidth}×${originalHeight}px). Must be at least 500×500px for distribution.`);
  }

  if (onProgress) {
    onProgress({ stage: 'analyzing', progress: 40, message: 'Verifying 1:1 aspect ratio & dimensions...' });
  }

  // 1:1 Aspect ratio check & safe center square cropping
  const isSquare = Math.abs(originalWidth - originalHeight) < 2; // Tolerance for subpixel variance

  let srcX = 0;
  let srcY = 0;
  let srcSize = Math.min(originalWidth, originalHeight);

  if (!isSquare) {
    // Perform clean center crop without stretching or distorting
    srcX = Math.floor((originalWidth - srcSize) / 2);
    srcY = Math.floor((originalHeight - srcSize) / 2);
  }

  if (onProgress) {
    onProgress({ stage: 'resizing', progress: 65, message: 'Resampling to 3000 × 3000 px JPG...' });
  }

  // High-performance Canvas rendering
  const canvas = document.createElement('canvas');
  canvas.width = 3000;
  canvas.height = 3000;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas 2D rendering context not available.');
  }

  // High quality image smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Fill with solid black background in case of transparent PNG/WEBP
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, 3000, 3000);

  // Draw square crop onto 3000x3000px canvas
  ctx.drawImage(image, srcX, srcY, srcSize, srcSize, 0, 0, 3000, 3000);

  if (onProgress) {
    onProgress({ stage: 'encoding', progress: 85, message: 'Encoding 3000 × 3000 px JPEG...' });
  }

  // Convert canvas to JPG blob at 0.95 quality
  const processedBlob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to generate JPEG blob from canvas.'));
      },
      'image/jpeg',
      0.95
    );
  });

  const processedFilename = 'cover_3000x3000.jpg';
  const processedFile = new File([processedBlob], processedFilename, { type: 'image/jpeg' });
  const previewUrl = URL.createObjectURL(processedBlob);

  if (onProgress) {
    onProgress({ stage: 'done', progress: 100, message: 'Cover art processed successfully.' });
  }

  return {
    processedFile,
    previewUrl,
    originalFilename: file.name,
    processedFilename,
    originalWidth,
    originalHeight,
    finalWidth: 3000,
    finalHeight: 3000,
    aspectRatio: '1:1',
    size: processedBlob.size,
    isCropped: !isSquare,
  };
}
